/**
 * A NAV-KULCSBEVITEL ÁLLAPOTA — a IV/D3 felületének motorja.
 *
 * ⚠️ EGY DOLGOT NEM TUD, ÉS EZ SZÁNDÉKOS: VISSZAOLVASNI. A `GET
 * /nav/credentials` csak azt adja meg, MELYIK cégnél van bekötés — értéket
 * nem, semmilyen alakban (`nav-osszekottetes.md` II/9, 2. alsó korlát).
 * Ezért nincs „töltsük ki az űrlapot a meglévő értékekkel" ág: nincs mivel.
 *
 * A KÖVETKEZMÉNYT KI KELL MONDANI A FELHASZNÁLÓNAK. Egy könyvelő, aki
 * visszatér az ablakhoz és üres mezőket lát ott, ahol korábban beírt valamit,
 * ELVESZETT ADATRA gondol — és újra beírja, esetleg emlékezetből, rosszul.
 * Ezért van a `writeOnlyNotice` az űrlapon, és ezért mutatja a cégkereső a
 * „be van kötve" jelzést: az az EGYETLEN visszajelzés, amit adhatunk.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';

/** Egy cég a keresőben — a backend `NavCredentialStatus`-a. */
export interface NavCredentialStatus {
  companyId: string;
  companyName: string;
  taxNumber: string;
  configured: boolean;
  /** ISO-szöveg, ahogy a JSON hozza. `null`, ha nincs bekötve. */
  configuredAt: string | null;
  hasExchangeKey: boolean;
}

/** A négy mező, ahogy az űrlap küldi. A cserekulcs elhagyható. */
export interface NavCredentialInput {
  login: string;
  password: string;
  signingKey: string;
  exchangeKey: string;
}

export type NavCredentialsStatus =
  | 'loading'
  | 'ready'
  | 'listFailed'
  | 'saving'
  | 'saved'
  | 'failed'
  | 'forbidden'
  | 'invalid'
  | 'keyRingMissing';

export interface NavCredentialsState {
  status: NavCredentialsStatus;
  companies: NavCredentialStatus[];
  /** A szerver üzenete a 400-as ághoz — a felület azt mutatja, nem sajátot. */
  detail: string;
  save: (companyId: string, input: NavCredentialInput) => void;
  /** Újratöltés mentés után: a „be van kötve" jelzésnek frissülnie kell. */
  reload: () => void;
}

/**
 * A KULCSGYŰRŰ HIÁNYA — `503 Service Unavailable`.
 *
 * ⚠️ EZ KORÁBBAN SZÖVEGRE ILLESZTETT, ÉS AZ HALOTT KÓD VOLT. A biztonsági
 * audit mérte ki (#24): a projektnek nincs globális kivétel-szűrője, tehát a
 * backend `NavCredentialKeyRingMissingError`-ja a Nest alapértelmezése
 * szerint `"Internal server error"`-ként ért ide — az ÜZENET sosem jutott el
 * a klienshez, a `message.includes('NAV_CREDENTIAL_KEYS')` tehát soha nem
 * talált volna.
 *
 * A backend azóta `503`-at ad rá, és ez pontosabb is annál, amit a szöveg
 * mondott: a kiszolgáló egy hiányzó BEÁLLÍTÁS miatt nem tudja elvégezni a
 * műveletet. Egy „próbáld újra" itt hazug lenne — az újrapróbálás soha nem
 * fog sikerülni, és a könyvelő nem tudja megjavítani.
 */
const KEY_RING_MISSING_STATUS = 503;

export function useNavCredentials(): NavCredentialsState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<NavCredentialsStatus>('loading');
  const [companies, setCompanies] = useState<NavCredentialStatus[]>([]);
  const [detail, setDetail] = useState('');

  /**
   * ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — ugyanaz a fogás, mint a
   * `useNavSync`-ben: egy `setStatus(current => …)` frissítőjében indított
   * kérés `StrictMode` alatt KÉTSZER futna le. Itt a második egy MÁSODIK
   * mentés lenne, ugyanazokkal a titkokkal.
   */
  const inFlight = useRef(false);

  /**
   * ⚠️ A LEZÁRT ABLAK NEM ÍR ÁLLAPOTOT. Enélkül a React figyelmeztet
   * („update on unmounted component"), és ami rosszabb: egy bezárt, majd
   * újra megnyitott ablak a RÉGI kérés válaszára állna át.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fail = useCallback(
    (caught: unknown, onError: (next: NavCredentialsStatus, message: string) => void) => {
      const httpStatus = caught instanceof ApiError ? caught.status : 0;
      const message = caught instanceof ApiError ? caught.message : '';

      if (httpStatus === 401) {
        // Nem a művelet hibája: a munkamenet járt le.
        signOut('expired');
        return;
      }
      if (httpStatus === 403) {
        onError('forbidden', '');
        return;
      }
      if (httpStatus === 400) {
        // A szerver üzenete magyarul jön (a DTO üzenetei), és pontosabb,
        // mint amit itt írni tudnánk: megmondja, MELYIK mező alakja rossz.
        onError('invalid', message);
        return;
      }
      onError(httpStatus === KEY_RING_MISSING_STATUS ? 'keyRingMissing' : 'failed', message);
    },
    [signOut]
  );

  const reload = useCallback(() => {
    setStatus('loading');

    void api<NavCredentialStatus[]>('/nav/credentials')
      .then((list) => {
        if (!alive.current) {
          return;
        }
        setCompanies(list);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (!alive.current) {
          return;
        }
        // A LISTA hibája külön ág: ilyenkor nincs is mit kiválasztani, tehát
        // az űrlapot sem érdemes megmutatni.
        fail(caught, (next) => setStatus(next === 'forbidden' ? 'forbidden' : 'listFailed'));
      });
  }, [fail]);

  /**
   * A LISTA ÚJRAOLVASÁSA AZ ÁLLAPOT MEGZAVARÁSA NÉLKÜL. (#34)
   *
   * ⚠️ MIÉRT NEM A `reload()` MEGY MENTÉS UTÁN. Az `loading`-ra vált, a
   * `saved` állapot pedig abból a státuszból SZÁRMAZIK — tehát a `reload()`
   * a nyugtázó panelt („Mentve: <cég>") azonnal eltüntetné, és a könyvelő
   * nem kapna visszajelzést arról, hogy a mentés sikerült. Épp ezért nem
   * futott eddig: a frissítés a `next()`-re maradt, és addig a kereső
   * „nincs bekötve"-t mutatott egy MÁR BEKÖTÖTT cégen (mérve 2026-09-14,
   * #26). A jelvény a legfontosabb visszajelzésünk — a kulcsot visszaolvasni
   * nem tudjuk (II/9) —, tehát pont az késett.
   *
   * Ez a menet CSAK a `companies`-t írja, a `status`-t nem. Így a nyugta
   * megmarad, a jelvény pedig azonnal zöldre vált.
   *
   * A HIBÁJA SZÁNDÉKOSAN NÉMA: a mentés MÁR SIKERÜLT, a szerveren a kulcs
   * ott van. Egy hibasáv itt azt sugallná, hogy a mentés bukott. A jelvény
   * ilyenkor annyit késik, amennyit eddig mindig — a következő `reload()`
   * behozza.
   */
  const refreshQuietly = useCallback(() => {
    void api<NavCredentialStatus[]>('/nav/credentials')
      .then((list) => {
        if (alive.current) {
          setCompanies(list);
        }
      })
      .catch(() => {
        /* lásd a fenti indoklást: a mentés sikerült, ezt nem jelentjük */
      });
  }, []);

  // NYITÁSKOR EGYSZER — és nem `useEffect(reload, [reload])` alakban: azt a
  // Biome szükségtelen függőségnek olvassa, a `lint-gate` pedig a
  // figyelmeztetést is hibának veszi.
  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(
    (companyId: string, input: NavCredentialInput) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setStatus('saving');
      setDetail('');

      void api<void>(`/nav/${companyId}/credentials`, {
        method: 'PUT',
        body: JSON.stringify({
          login: input.login,
          password: input.password,
          signingKey: input.signingKey,
          // ⚠️ ÜRES MEZŐ = NINCS CSEREKULCS, tehát a kulcsot KI SEM KÜLDJÜK.
          // Egy `exchangeKey: ''` a szerveren a `@Matches` mintán bukna el
          // („nem tartalmazhat szóközt"), ami félrevezető hibaüzenet arra,
          // hogy a mezőt egyszerűen nem töltötték ki.
          ...(input.exchangeKey ? { exchangeKey: input.exchangeKey } : {}),
        }),
      })
        .then(() => {
          if (!alive.current) {
            return;
          }
          setStatus('saved');
          // A KERESŐ JELVÉNYE AZONNAL ZÖLDRE VÁLT (#34) — a nyugta közben áll.
          refreshQuietly();
        })
        .catch((caught: unknown) => {
          if (!alive.current) {
            return;
          }
          fail(caught, (next, message) => {
            setStatus(next);
            setDetail(message);
          });
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [fail, refreshQuietly]
  );

  return { status, companies, detail, save, reload };
}
